import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Btn, Spinner } from "@/app/ui";
import { money } from "@/lib/format";
import { Plus, Trash2 } from "lucide-react";

export default function JobCosting({ job, sym, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [hours, setHours] = useState("");
  const [rate, setRate] = useState("");
  const [mats, setMats] = useState([]);
  const [invoiceId, setInvoiceId] = useState("");

  useEffect(() => {
    let alive = true;
    api.get(`/jobs/${job.id}/costing`).then((r) => {
      if (!alive) return;
      const d = r.data;
      setData(d);
      setHours(d.labour_hours ?? "");
      setRate(d.labour_rate ?? "");
      setMats(d.material_lines || []);
      setInvoiceId(d.invoice ? d.invoice.id : "");
      setLoading(false);
    }).catch((e) => { toast.error(apiError(e.response?.data?.detail)); onClose(); });
    return () => { alive = false; };
  }, [job.id]); // eslint-disable-line

  const addMat = () => setMats([...mats, { name: "", quantity: 1, unit_cost: 0 }]);
  const setMat = (i, k, v) => setMats(mats.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)));
  const rmMat = (i) => setMats(mats.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        labour_hours: hours === "" ? null : Number(hours),
        labour_rate: rate === "" ? null : Number(rate),
        material_lines: mats.map((m) => ({ name: m.name, quantity: Number(m.quantity) || 0, unit_cost: Number(m.unit_cost) || 0 })),
        invoice_id: invoiceId || null,
      };
      const { data: d } = await api.put(`/jobs/${job.id}/costing`, payload);
      setData(d);
      toast.success("Costing saved");
      onSaved && onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const liveHours = hours === "" ? (data?.auto_labour_hours || 0) : Number(hours);
  const liveRate = rate === "" ? 0 : Number(rate);
  const labourCost = liveHours * liveRate;
  const matCost = mats.reduce((s, m) => s + (Number(m.quantity) || 0) * (Number(m.unit_cost) || 0), 0);
  const expCost = data?.expense_cost || 0;
  const totalCost = labourCost + matCost + expCost;
  const selInv = (data?.available_invoices || []).find((i) => i.id === invoiceId);
  const revenue = selInv ? selInv.total : (data?.quoted_value || 0);
  const margin = revenue - totalCost;
  const marginPct = revenue ? (margin / revenue) * 100 : 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto amden-scroll">
        <DialogHeader><DialogTitle>Costs &amp; margin · {job.number}</DialogTitle></DialogHeader>
        {loading || !data ? <Spinner /> : (
          <div className="space-y-5 pt-1" data-testid="job-costing-body">
            <div>
              <h4 className="text-sm font-semibold text-navy mb-1">Labour</h4>
              <p className="text-xs text-slate-400 mb-2">Auto from time clock: <span className="font-mono">{data.auto_labour_hours}h</span>. Leave hours blank to use this.</p>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-500">Hours</label><input type="number" value={hours} onChange={(e) => setHours(e.target.value)} placeholder={String(data.auto_labour_hours)} data-testid="costing-hours" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono" /></div>
                <div><label className="text-xs text-slate-500">Rate / hr ({sym})</label><input type="number" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="costing-rate" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono" /></div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2"><h4 className="text-sm font-semibold text-navy">Materials</h4><button onClick={addMat} data-testid="costing-add-material" className="text-xs text-blue-accent inline-flex items-center gap-1"><Plus size={13} /> Add</button></div>
              {mats.length === 0 && <p className="text-xs text-slate-400">No materials added.</p>}
              <div className="space-y-2">
                {mats.map((m, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input value={m.name} onChange={(e) => setMat(i, "name", e.target.value)} placeholder="Item" className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
                    <input type="number" value={m.quantity} onChange={(e) => setMat(i, "quantity", e.target.value)} placeholder="Qty" className="w-16 rounded-lg border border-slate-300 px-2 py-1.5 text-sm font-mono" />
                    <input type="number" value={m.unit_cost} onChange={(e) => setMat(i, "unit_cost", e.target.value)} placeholder="Cost" className="w-20 rounded-lg border border-slate-300 px-2 py-1.5 text-sm font-mono" />
                    <button onClick={() => rmMat(i)} className="text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            </div>

            {data.expense_lines?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-navy mb-2">Linked expenses</h4>
                <div className="space-y-1">
                  {data.expense_lines.map((e) => (
                    <div key={e.id} className="flex justify-between text-sm"><span className="text-slate-600">{e.description}</span><span className="font-mono">{money(e.amount, sym)}</span></div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h4 className="text-sm font-semibold text-navy mb-1">Revenue</h4>
              <label className="text-xs text-slate-500">Link an invoice, or use the quoted value</label>
              <select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} data-testid="costing-invoice" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white text-sm">
                <option value="">Use quoted value ({money(data.quoted_value, sym)})</option>
                {(data.available_invoices || []).map((i) => <option key={i.id} value={i.id}>{i.number} · {i.customer_name} · {money(i.total, sym)}</option>)}
              </select>
            </div>

            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-1.5 text-sm" data-testid="costing-summary">
              <div className="flex justify-between"><span className="text-slate-500">Labour</span><span className="font-mono">{money(labourCost, sym)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Materials</span><span className="font-mono">{money(matCost, sym)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Expenses</span><span className="font-mono">{money(expCost, sym)}</span></div>
              <div className="flex justify-between font-semibold text-navy border-t border-slate-200 pt-1.5"><span>Total cost</span><span className="font-mono">{money(totalCost, sym)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Revenue</span><span className="font-mono">{money(revenue, sym)}</span></div>
              <div className={`flex justify-between font-bold border-t border-slate-200 pt-1.5 ${margin >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="costing-margin"><span>Gross margin</span><span className="font-mono">{money(margin, sym)} · {marginPct.toFixed(1)}%</span></div>
            </div>
          </div>
        )}
        <DialogFooter><Btn variant="secondary" onClick={onClose}>Close</Btn><Btn variant="accent" onClick={save} disabled={saving || loading} data-testid="costing-save">{saving ? "Saving…" : "Save costing"}</Btn></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
